import { useState, useEffect, useMemo } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Search, Database, Settings, Menu, X, RefreshCw, ChevronLeft, ChevronDown, ChevronRight } from "lucide-react"
import { ROUTES } from "@/routes/path"
import resourceTypes from "@/lib/data/resources.json"
import { useListResources } from "@/hooks/useResources"
import { useListContainers } from "@/hooks/useResources"
import { useListContainerFields, useResyncResource } from "@/hooks/useResources"
import { FieldSettingsModal } from "./components/field-settings-modal"
import { ColumnsTab } from "./components/columns-tab"
import { OriginalSchemaTab } from "./components/original-schema-tab"
import { ContainerDescription } from "./components/container-description"
import { ResyncConfirmationDialog } from "@/components/ui/resync-confirmation-dialog"
import { useQueryClient } from "@tanstack/react-query"
import { useToast } from "@/hooks/useToast"
import { ResourceContainer, ResourceField } from "@/types/api"
import { cn } from "@/lib/utils"

export default function ManageResourcePage() {
  const { id, containerId } = useParams<{ id: string; containerId?: string }>()
  const navigate = useNavigate()

  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedContainer, setSelectedContainer] = useState<ResourceContainer | null>(null)
  const [activeTab, setActiveTab] = useState<"columns" | "original">("columns")
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)
  const [selectedFieldForSettings, setSelectedFieldForSettings] = useState<ResourceField | null>(null)
  const [showResyncDialog, setShowResyncDialog] = useState(false)
  const [collapsedNamespaces, setCollapsedNamespaces] = useState<Record<string, boolean>>({})

  const { toast } = useToast()
  const resyncResourceMutation = useResyncResource()

  // Fetch resources to get the current resource
  const { data: resources } = useListResources()
  const resource = resources?.find((r) => r.id === id)

  // Fetch containers for this resource
  const { data: containersData, isLoading: containersLoading } = useListContainers(id || "")

  // Fetch fields for selected container
  const { data: fieldsData, isLoading: fieldsLoading } = useListContainerFields(id || "", selectedContainer?.id || "")

  // Clear selected container when resource ID changes
  useEffect(() => {
    setSelectedContainer(null)
  }, [id])

  // Update selected container when containers data changes
  useEffect(() => {
    if (!containersData || containersData.length === 0) {
      return
    }

    // If there's a containerId in the URL, try to select that container
    if (containerId) {
      const urlContainer = containersData.find((container) => container.id === containerId)
      if (urlContainer) {
        if (selectedContainer?.id !== urlContainer.id) {
          setSelectedContainer(urlContainer)
        }
        return
      }
    }

    // Auto-select first container if none selected, or if the current selected container
    // doesn't exist in the new containers list (e.g., after switching resources)
    const currentContainerExists =
      selectedContainer && containersData.some((container) => container.id === selectedContainer.id)

    if (!selectedContainer || !currentContainerExists) {
      setSelectedContainer(containersData[0])
    }
  }, [containersData, selectedContainer, containerId])

  const filteredContainers = useMemo(
    () =>
      (containersData || []).filter((container) => container.name.toLowerCase().includes(searchQuery.toLowerCase())),
    [containersData, searchQuery]
  )

  const getContainerNamespace = (container: ResourceContainer): string => {
    const namespace = container.meta?.namespace
    if (!namespace || !namespace.trim()) {
      return "public"
    }
    return namespace
  }

  const groupedContainers = useMemo(() => {
    const groups: Record<string, ResourceContainer[]> = {}

    for (const container of filteredContainers) {
      const namespace = getContainerNamespace(container)
      if (!groups[namespace]) {
        groups[namespace] = []
      }
      groups[namespace].push(container)
    }

    const sortedNamespaces = Object.keys(groups).sort((a, b) => {
      if (a === "public") return -1
      if (b === "public") return 1
      return a.localeCompare(b)
    })

    return sortedNamespaces.map((namespace) => ({
      namespace,
      containers: groups[namespace],
    }))
  }, [filteredContainers])

  useEffect(() => {
    setCollapsedNamespaces((prev) => {
      let hasChanges = false
      const next = { ...prev }

      for (const group of groupedContainers) {
        if (next[group.namespace] === undefined) {
          next[group.namespace] = false
          hasChanges = true
        }
      }

      return hasChanges ? next : prev
    })
  }, [groupedContainers])

  const toggleNamespace = (namespace: string) => {
    setCollapsedNamespaces((prev) => ({
      ...prev,
      [namespace]: !prev[namespace],
    }))
  }

  const getResourceIcon = () => {
    if (!resource) return <Database className="h-5 w-5" />

    const resourceType = resourceTypes.find((rt) => rt.id === resource.type)
    if (resourceType?.icon) {
      return <img src={resourceType.icon} alt={resource.name} className="h-5 w-5" />
    }
    return <Database className="h-5 w-5" />
  }

  const handleFieldUpdate = (
    _fieldId: string,
    _updates: {
      description: string
      display_name: string
      visibility: string
      data_type: string
      semantic_type: string
    }
  ) => {
    // This function is passed to the ColumnsTab component
    // The actual field update logic is now handled within the ColumnsTab component
  }

  const handleResourceChange = (newResourceId: string) => {
    if (newResourceId !== id) {
      navigate(ROUTES.DASHBOARD.RESOURCES.MANAGE(newResourceId))
    }
  }

  const handleContainerSelect = (container: ResourceContainer) => {
    setSelectedContainer(container)
    setIsMobileSidebarOpen(false) // Close mobile sidebar when container is selected

    // Navigate to the container URL
    navigate(ROUTES.DASHBOARD.RESOURCES.MANAGE_CONTAINER(id!, container.id))
  }

  // Skeleton loader component for containers
  const ContainerSkeleton = () => (
    <div className="space-y-2">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="w-full animate-pulse rounded-lg bg-gray-100 p-3 dark:bg-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="h-4 w-4 rounded bg-gray-300 dark:bg-gray-600"></div>
              <div className="h-4 w-20 rounded bg-gray-300 dark:bg-gray-600"></div>
            </div>
            <div className="h-4 w-6 rounded bg-gray-300 dark:bg-gray-600"></div>
          </div>
        </div>
      ))}
    </div>
  )

  return (
    <div className="flex h-screen">
      {/* Mobile Header */}
      <div className="fixed left-0 right-0 top-0 z-50 border-b border-gray-200 p-4 dark:border-gray-700 lg:hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(ROUTES.DASHBOARD.RESOURCES.INDEX)}
              className="p-0 px-2"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center space-x-2">
              {getResourceIcon()}
              <span className="text-sm font-normal">{resource?.name || "Loading..."}</span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
            className="p-2"
          >
            {isMobileSidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Left Sidebar - Containers */}
      <div
        className={cn(
          "w-80 flex-col border-r border-gray-200 bg-white dark:border-gray-700 dark:bg-black",
          "lg:flex",
          isMobileSidebarOpen ? "fixed left-0 top-0 z-50 flex h-full" : "hidden"
        )}
      >
        {/* Header */}
        <div className="border-b border-gray-200 p-4 dark:border-gray-700">
          <div className="mb-4 flex items-center justify-between">
            <div className="hidden lg:block">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(ROUTES.DASHBOARD.RESOURCES.INDEX)}
                className="p-0 px-1"
              >
                <ChevronLeft className="mr-0.5 h-4 w-4" />
                Back
              </Button>
            </div>
            <div className="flex items-end space-x-1.5">
              {getResourceIcon()}
              <Select value={id || ""} onValueChange={handleResourceChange}>
                <SelectTrigger className="h-auto w-40 border-0 p-0 text-sm font-medium shadow-none focus:border-0 focus:ring-0 focus:ring-offset-0">
                  <SelectValue>
                    {resource ? (
                      <span className="whitespace-nowrap text-sm">{resource.name}</span>
                    ) : (
                      <div className="h-4 w-24 animate-pulse rounded bg-gray-300 dark:bg-gray-600"></div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {resources?.map((res) => {
                    const resourceType = resourceTypes.find((rt) => rt.id === res.type)
                    return (
                      <SelectItem key={res.id} value={res.id}>
                        <div className="flex items-center space-x-2">
                          {resourceType?.icon ? (
                            <img src={resourceType.icon} alt={res.name} className="h-4 w-4" />
                          ) : (
                            <Database className="h-4 w-4" />
                          )}
                          <span>{res.name}</span>
                        </div>
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 stroke-gray-900 text-gray-600" />
            <Input
              placeholder="Find a table"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-2"
            />
          </div>
        </div>

        {/* Container List */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-2">
            <div className="mb-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400">
              {containersLoading ? (
                <div className="h-4 w-16 animate-pulse rounded bg-gray-300 dark:bg-gray-600"></div>
              ) : (
                `${filteredContainers.length} TABLES`
              )}
            </div>
            {containersLoading ? (
              <ContainerSkeleton />
            ) : filteredContainers.length === 0 ? (
              <div className="py-8 text-center">
                <Database className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {searchQuery ? "No tables found" : "No tables available"}
                </p>
              </div>
            ) : (
              groupedContainers.map((group) => (
                <div key={group.namespace} className="mb-1">
                  <button
                    type="button"
                    onClick={() => toggleNamespace(group.namespace)}
                    className="mb-1 flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-900"
                  >
                    <div className="flex items-center gap-2">
                      {collapsedNamespaces[group.namespace] ? (
                        <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                      )}
                      <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {group.namespace}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{group.containers.length}</span>
                  </button>

                  {!collapsedNamespaces[group.namespace] &&
                    group.containers.map((container) => (
                      <button
                        key={container.id}
                        onClick={() => handleContainerSelect(container)}
                        className={`mb-0.5 ml-5 w-[calc(100%-1.25rem)] rounded-md px-2.5 py-1.5 text-left transition-colors ${
                          selectedContainer?.id === container.id
                            ? "border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/50"
                            : "hover:bg-gray-50 dark:hover:bg-gray-900"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Database className="h-4 w-4 text-gray-500" />
                            <span className="text-sm font-normal">{container.name}</span>
                          </div>
                        </div>
                      </button>
                    ))}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Right Content - Fields */}
      <div className="flex flex-1 flex-col pt-14 lg:ml-0 lg:pt-0">
        <div className="mt-8 flex items-center justify-end space-x-2 px-5">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowResyncDialog(true)}
            disabled={resyncResourceMutation.isPending}
            className="hidden sm:flex"
          >
            {resyncResourceMutation.isPending ? (
              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Resync
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (id) {
                resyncResourceMutation.mutate(id, {
                  onSuccess: () => {
                    toast({
                      title: "Resource resync initiated",
                      description: "The resource is being resynced. This may take a few minutes.",
                    })
                    queryClient.invalidateQueries({
                      queryKey: ["resources"],
                    })
                    queryClient.invalidateQueries({
                      queryKey: ["resources/containers", id],
                    })
                    if (selectedContainer) {
                      queryClient.invalidateQueries({
                        queryKey: ["resources/container-fields", id, selectedContainer.id],
                      })
                    }
                  },
                  onError: () => {
                    toast({
                      title: "Resync failed",
                      description: "Failed to initiate resource resync. Please try again.",
                      variant: "destructive",
                    })
                  },
                })
              }
            }}
            disabled={resyncResourceMutation.isPending}
            className="sm:hidden"
          >
            {resyncResourceMutation.isPending ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="hidden sm:flex"
            onClick={() => navigate(ROUTES.DASHBOARD.RESOURCES.SETTINGS(id || ""))}
          >
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="sm:hidden"
            onClick={() => navigate(ROUTES.DASHBOARD.RESOURCES.SETTINGS(id || ""))}
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>
        {selectedContainer ? (
          <>
            {/* Container Header */}
            <div className="border-b border-gray-200 p-4 dark:border-gray-700 lg:px-6">
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white lg:text-2xl">
                  {selectedContainer.name}
                </h1>
                <ContainerDescription resourceId={id || ""} container={selectedContainer} />
              </div>

              {/* Tabs */}
              <div className="mt-4 flex space-x-6">
                <button
                  onClick={() => setActiveTab("columns")}
                  className={cn(
                    "border-b-2 pb-2 text-sm font-medium transition-colors",
                    activeTab === "columns"
                      ? "border-gray-800 text-gray-900 dark:border-white dark:text-white"
                      : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:border-white dark:hover:text-white"
                  )}
                >
                  Columns
                </button>
                <button
                  onClick={() => setActiveTab("original")}
                  className={cn(
                    "border-b-2 pb-2 text-sm font-medium transition-colors",
                    activeTab === "original"
                      ? "border-gray-800 text-gray-900 dark:border-white dark:text-white"
                      : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:border-white dark:hover:text-white"
                  )}
                >
                  Original schema
                </button>
              </div>
            </div>

            {/* Fields Table */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-4 lg:p-6">
                {activeTab === "columns" ? (
                  <ColumnsTab
                    fieldsData={fieldsData}
                    fieldsLoading={fieldsLoading}
                    resource={resource}
                    selectedContainer={selectedContainer}
                    onFieldUpdate={handleFieldUpdate}
                    onFieldSettingsOpen={setSelectedFieldForSettings}
                  />
                ) : activeTab === "original" ? (
                  <OriginalSchemaTab fieldsData={fieldsData} fieldsLoading={fieldsLoading} />
                ) : null}
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <div className="text-center">
              <Database className="mx-auto mb-4 h-12 w-12 text-gray-400" />
              <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">Select a table</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Choose a table from the sidebar to view and edit its columns
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Field Settings Modal */}
      <FieldSettingsModal
        open={!!selectedFieldForSettings}
        onOpenChange={(open) => !open && setSelectedFieldForSettings(null)}
        field={selectedFieldForSettings}
        resourceId={id || ""}
        container={selectedContainer}
        onFieldUpdate={handleFieldUpdate}
      />

      {/* Resync Confirmation Dialog */}
      <ResyncConfirmationDialog
        open={showResyncDialog}
        onOpenChange={setShowResyncDialog}
        resourceName={resource?.name || ""}
        onConfirm={() => {
          if (id) {
            resyncResourceMutation.mutate(id, {
              onSuccess: () => {
                toast({
                  title: "Resource resync initiated",
                  description: "The resource is being resynced. This may take a few minutes.",
                })
                // Invalidate queries to refresh data
                queryClient.invalidateQueries({
                  queryKey: ["resources"],
                })
                queryClient.invalidateQueries({
                  queryKey: ["resources/containers", id],
                })
                if (selectedContainer) {
                  queryClient.invalidateQueries({
                    queryKey: ["resources/container-fields", id, selectedContainer.id],
                  })
                }
                setShowResyncDialog(false)
              },
              onError: () => {
                toast({
                  title: "Resync failed",
                  description: "Failed to initiate resource resync. Please try again.",
                  variant: "destructive",
                })
              },
            })
          }
        }}
        isResyncing={resyncResourceMutation.isPending}
      />
    </div>
  )
}
